import type { PrefetchEntry } from '@/lib/prefetch/modulePrefetch'
import { useEffect, useRef } from 'react'
import { createLogger } from '@/lib/logger'
import {
  coreModulePrefetchEntries,
  moduleUiLazyPrefetchEntries,
  secondaryModulePrefetchEntries,
} from '@/lib/prefetch/modulePrefetch'
import { isMainWindow, isPopupWindow, isTauri } from '@/lib/utils/tauri'

const logger = createLogger('hooks/useTauriModulePrefetch')

export const useTauriModulePrefetch = (): void => {
  const hasPrefetched = useRef(false)

  useEffect(() => {
    if (hasPrefetched.current || !isTauri() || isPopupWindow()) {
      return
    }

    let cancelled = false
    const prefetchEntriesBatched = async (
      entries: PrefetchEntry[],
      batchSize = 8,
    ) => {
      for (let i = 0; i < entries.length; i += batchSize) {
        if (cancelled) return

        const batch = entries.slice(i, i + batchSize)
        await Promise.all(
          batch.map(async entry => {
            if (cancelled) return
            try {
              await entry.load()
            } catch (error) {
              logger.debug(`Prefetch failed for ${entry.id}:`, error)
            }
          }),
        )
      }
    }

    void (async () => {
      const isMain = await isMainWindow()
      if (!isMain || cancelled) return

      hasPrefetched.current = true
      logger.debug('Starting full Tauri module UI preload')

      await prefetchEntriesBatched(coreModulePrefetchEntries, 6)
      await prefetchEntriesBatched(secondaryModulePrefetchEntries, 6)
      await prefetchEntriesBatched(moduleUiLazyPrefetchEntries, 8)
    })()

    return () => {
      cancelled = true
    }
  }, [])
}
